import React, { ComponentType, useCallback, useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { View } from "@com.mgmtp.a12.client/client-core/lib/core/view";
import { TreeModel } from "@com.mgmtp.a12.treeengine/treeengine-core/lib/core/models";
import { Events, UIStateSelector } from "@com.mgmtp.a12.treeengine/treeengine-core/lib/core/store";
import {
	ComponentMap,
	ContextMenu,
	DefaultComponentMap,
	DefaultWidgetMap,
	FlattenNodeRow,
	RightClickContextMenu,
	RowAction,
	useTreeEngineContext,
	WidgetMap
} from "@com.mgmtp.a12.treeengine/treeengine-core/lib/core/view";
import {
	TreeEngineActions,
	TreeEngineFactories,
	TreeEngineSelectors
} from "@com.mgmtp.a12.treeengine/treeengine-core/lib/extensions/client";
import { LocalizerContext } from "@com.mgmtp.a12.utils/utils-localization-react/lib/main";
import { ButtonProps } from "@com.mgmtp.a12.widgets/widgets-core/lib/button";
import { addPrefix, joinClassNames } from "@com.mgmtp.a12.widgets/widgets-core/lib/common";
import { ListItemProps } from "@com.mgmtp.a12.widgets/widgets-core/lib/list";
import { PopUpMenuProps } from "@com.mgmtp.a12.widgets/widgets-core/lib/pop-up-menu";

import { ActivityIdContext } from "../../../component/ActivityContext";
import { CustomContentBox } from "../../../component/CustomContentBox";
import { ScopeBasedVisibilityDecorator } from "../../../component/ScopeBasedVisibilityDecorator";
import { RESOURCE_KEYS } from "../../../localization";
import { assertNonNullable } from "../../../utilities/assert";
import { hasUpdateDocumentPermission } from "../../../utilities/permissionUtils";
import { selectHasNeededPermissions, selectIsAdmin, selectPermissionsForFv } from "../../../utilities/user";
import { TEXTBAUSTEINE_VERFAHREN_DOCUMENT } from "../../../utilities/variables/documentModelName";
import { VISIBILITY_SCOPE } from "../../../utilities/variables/modulfRuntimeAnnotations";
import { importTextbausteine } from "../../textbausteine/action";
import {
	isTextbausteinDocumentWithIdentifier,
	TextbausteinWitchIdentifier
} from "../../textbausteine/textbausteineTypeGuard";

import { TreeEngineRowActionContext } from "./contexts";

export const FilteredTreeEngine: View.ViewComponent = function FilteredTreeEngine(props) {
	const treeEngineState = useSelector(TreeEngineSelectors.engineState(props.activityId));

	const verfahrenFvKeyMap = useMemo(() => {
		const fvKeyMap: Record<string, string> = {};
		Object.entries(treeEngineState?.data ?? {})
			.filter(([key]) => key === "Verfahren_D")
			.flatMap(([key, value]) => (value ? Object.values(value) : []))
			.filter(
				(it): it is TextbausteinWitchIdentifier => it !== undefined && isTextbausteinDocumentWithIdentifier(it)
			)
			.forEach(it => (fvKeyMap[it.identifier.id] = it.document.root.correlatedFachverfahrenKey));
		return fvKeyMap;
	}, [treeEngineState?.data]);

	const widgetMap: WidgetMap = useMemo(() => {
		return {
			...DefaultWidgetMap,
			PopUpMenu: CustomPopUpMenu,
			ContentBox: CustomContentBox,
			ListItem: CustomListItem,
			ContextMenu: CustomContextMenu
		};
	}, []);

	const componentMap: ComponentMap = useMemo(() => {
		return {
			...DefaultComponentMap,
			RowAction: CustomRowAction,
			ContextMenu: CustomContextMenu,
			RightClickContextMenu: CustomRightClickContextMenu
		};
	}, []);

	if (!treeEngineState) {
		return null;
	}
	return (
		<ActivityIdContext.Provider value={props.activityId}>
			<FvKeyMapContext.Provider value={verfahrenFvKeyMap}>
				<TreeEngineFactories.ViewComponent {...props} widgetMap={widgetMap} componentMap={componentMap} />
			</FvKeyMapContext.Provider>
		</ActivityIdContext.Provider>
	);
};

const CustomPopUpMenu: React.ComponentType<PopUpMenuProps> = function CustomBodyCell(props) {
	const { onVisibilityChange } = props;
	const { localizer } = React.useContext(LocalizerContext);
	const [isOpen, setIsOpen] = React.useState(false);

	const customOnVisibilityChange = React.useCallback(
		(visible: boolean) => {
			setIsOpen(visible);

			if (!onVisibilityChange) {
				return;
			}

			onVisibilityChange(visible);
		},
		[onVisibilityChange]
	);

	return (
		<DefaultWidgetMap.PopUpMenu
			{...props}
			onVisibilityChange={customOnVisibilityChange}
			triggerButtonTitle={localizer({
				key: isOpen
					? RESOURCE_KEYS.features.popUpMenu.expandAll.closeTitle
					: RESOURCE_KEYS.features.popUpMenu.expandAll.openTitle
			})}
		/>
	);
};

const CustomListItem: React.ComponentType<ListItemProps> = function CustomBodyCell(props) {
	const dispatch = useDispatch();

	const treeEngineRowAction = useContext(TreeEngineRowActionContext);
	const activityId = useContext(ActivityIdContext);

	const onClick = React.useCallback(() => {
		const input = document.createElement("input");
		input.type = "file";
		input.accept = "application/json";
		input.onchange = event => {
			if (!(event.target instanceof HTMLInputElement)) {
				return;
			}
			const files = Array.from(event.target.files ?? []);
			if (!files || !treeEngineRowAction || !activityId) {
				return;
			}
			files[0]
				.text()
				.then(fileContent => JSON.parse(fileContent))
				.then(textbausteinImportData =>
					dispatch(
						importTextbausteine({
							nodeIdentifier: treeEngineRowAction.nodeIdentifier,
							activityId,
							textbausteinImportData: textbausteinImportData
						})
					)
				);
		};
		input.click();
	}, [dispatch, activityId]);

	if (treeEngineRowAction?.eventName === "import_textbausteine") {
		return <DefaultWidgetMap.ListItem {...props} onClick={onClick} />;
	}

	return <DefaultWidgetMap.ListItem {...props} />;
};

const CustomRowAction: React.ComponentType<RowAction.Props> = function CustomRowAction(props) {
	const eventName = TreeModel.TreeNodeEventActionButton.isAssignableFrom(props.rowActionModel)
		? props.rowActionModel.event
		: undefined;
	if (eventName === "event_move") {
		return <ReorderButton {...props} />;
	} else {
		return <GeneralButton {...props} />;
	}
};

const GeneralButton: ComponentType<RowAction.Props> = function GeneralButton(props) {
	const eventName = TreeModel.TreeNodeEventActionButton.isAssignableFrom(props.rowActionModel)
		? props.rowActionModel.event
		: undefined;

	const value = useMemo(
		() => (eventName ? { eventName, nodeIdentifier: props.row.data.nodeIdentifier } : undefined),
		[eventName, props.row.data]
	);
	const fvKey = useFvKey(props.row.data.nodePath);

	const ScopeBasedRowAction = useMemo(() => {
		return ScopeBasedVisibilityDecorator(DefaultComponentMap.RowAction, t => t.rowActionModel.annotations, fvKey);
	}, [fvKey]);

	return (
		<TreeEngineRowActionContext.Provider value={value}>
			<ScopeBasedRowAction {...props} />
		</TreeEngineRowActionContext.Provider>
	);
};

const ReorderButton: ComponentType<RowAction.Props> = function ReorderButton(props) {
	const dispatch = useDispatch();

	const { Button, Icon, ListItem } = useTreeEngineContext(context => context.widgetMap);
	const activityId = useContext(ActivityIdContext);
	assertNonNullable(activityId);

	const onClickUp = useCallback<NonNullable<ButtonProps["onClick"]>>(
		event => {
			dispatch(
				TreeEngineActions.event({
					activityId,
					engineAction: Events.onNodeEventButtonClicked({
						...props.row.data,
						button: { type: "event", event: "event_move_up" }
					})
				})
			);
			event.stopPropagation();
		},
		[dispatch, activityId, props.row.data]
	);
	const onClickDown = useCallback<NonNullable<ButtonProps["onClick"]>>(
		event => {
			dispatch(
				TreeEngineActions.event({
					activityId,
					engineAction: Events.onNodeEventButtonClicked({
						...props.row.data,
						button: { type: "event", event: "event_move_down" }
					})
				})
			);
			event.stopPropagation();
		},
		[dispatch, activityId, props.row.data]
	);

	const disabled = useSelector(UIStateSelector.disabled());
	const readonly = useSelector(UIStateSelector.readonly());

	const rowActionStateGetter = useTreeEngineContext(context => context.rowActionStateGetter);
	const rowDisabled = rowActionStateGetter?.({ row: props.row, action: props.rowActionModel }).disabled;
	const fvKey = useFvKey(props.row.data.nodePath);
	const hasMovePerm = useSelector(selectHasNeededPermissions(hasUpdateDocumentPermission, fvKey));

	return props.displayAsPopupEntry ? (
		<>
			<ListItem
				key="up"
				id={props.row.id + "_up"}
				graphic={<Icon>keyboard_arrow_up</Icon>}
				text="Hochschieben"
				disabled={disabled || readonly || rowDisabled || !props.row.predecessor || !hasMovePerm}
				onClick={onClickUp}
			/>
			<ListItem
				key="down"
				id={props.row.id + "_down"}
				graphic={<Icon>keyboard_arrow_down</Icon>}
				text="Runterschieben"
				disabled={disabled || readonly || rowDisabled || !props.row.successor || !hasMovePerm}
				onClick={onClickDown}
			/>
		</>
	) : (
		<div className={joinClassNames(addPrefix("h_inlineBlock"), addPrefix("h_middleAlign"))} key="move">
			<Button
				key="up"
				id={props.row.id + "_up"}
				icon={<Icon>keyboard_arrow_up</Icon>}
				title="Hochschieben"
				className={addPrefix("-u-width-auto")}
				disabled={disabled || readonly || rowDisabled || !props.row.predecessor || !hasMovePerm}
				onClick={onClickUp}
			/>
			<Button
				key="down"
				id={props.row.id + "_down"}
				icon={<Icon>keyboard_arrow_down</Icon>}
				title="Runterschieben"
				className={addPrefix("-u-width-auto")}
				disabled={disabled || readonly || rowDisabled || !props.row.successor || !hasMovePerm}
				onClick={onClickDown}
			/>
		</div>
	);
};

const CustomContextMenu: React.ComponentType<ContextMenu.Props> = function CustomContextMenu(props) {
	const fvKey = useFvKey(props.row.data.nodePath);
	const permissions = useSelector(selectPermissionsForFv(fvKey));
	const isAdminUser = useSelector(selectIsAdmin);

	const childrenCount = props.contextMenuModel.groups
		.flatMap(it => it.actions)
		.filter(it => !hideAction(it, permissions)).length;

	if (childrenCount === 0 && !isAdminUser) {
		return <></>;
	}

	const isLastTopLevelKategorie = isLastKategorieUnderVerfahren(props.row);

	if (isLastTopLevelKategorie) {
		const filteredContextMenuModel = {
			...props.contextMenuModel,
			groups: props.contextMenuModel.groups
				.map(group => ({
					...group,
					actions: group.actions.filter(action => !isDeleteNodeAction(action))
				}))
				.filter(group => group.actions.length > 0)
		};
		return <DefaultComponentMap.ContextMenu {...props} contextMenuModel={filteredContextMenuModel} />;
	}

	return <DefaultComponentMap.ContextMenu {...props} />;
};

const CustomRightClickContextMenu: React.ComponentType<RightClickContextMenu.Props> =
	function CustomRightClickContextMenu(props) {
		const fvKey = useFvKey(props.row.data.nodePath);
		const permissions = useSelector(selectPermissionsForFv(fvKey));
		const isAdminUser = useSelector(selectIsAdmin);

		const contextMenuModel = props.row.nodeModel.contextMenu;

		const childrenCount = (contextMenuModel?.groups ?? [])
			.flatMap(it => it.actions)
			.filter(it => !hideAction(it, permissions)).length;

		if (childrenCount === 0 && !isAdminUser) {
			return <></>;
		}

		const isLastTopLevelKategorie = isLastKategorieUnderVerfahren(props.row);

		if (isLastTopLevelKategorie && contextMenuModel) {
			const filteredContextMenuModel = {
				...contextMenuModel,
				groups: contextMenuModel.groups
					.map(group => ({
						...group,
						actions: group.actions.filter(action => !isDeleteNodeAction(action))
					}))
					.filter(group => group.actions.length > 0)
			};
			const filteredRow: FlattenNodeRow = {
				...props.row,
				nodeModel: { ...props.row.nodeModel, contextMenu: filteredContextMenuModel }
			};
			return <DefaultComponentMap.RightClickContextMenu {...props} row={filteredRow} />;
		}

		return <DefaultComponentMap.RightClickContextMenu {...props} />;
	};

function isDeleteNodeAction(action: { type: string; event?: string }): boolean {
	return TreeModel.TreeNodeEventActionButton.isAssignableFrom(action) && action.event === "event_delete_node";
}

function isLastKategorieUnderVerfahren(row: FlattenNodeRow): boolean {
	const nodePath = row.data.nodePath;

	// Check if the node is a direct child of Verfahren_D (second element in path)
	const isUnderVerfahren =
		nodePath.length >= 2 &&
		(nodePath[0]?.type === TEXTBAUSTEINE_VERFAHREN_DOCUMENT ||
			nodePath[0]?.id?.includes(TEXTBAUSTEINE_VERFAHREN_DOCUMENT));

	// Find index of Verfahren_D in path and ensure current node is its direct child
	const verfahrenIndex = nodePath.findIndex(
		entry => entry.type === TEXTBAUSTEINE_VERFAHREN_DOCUMENT || entry.id?.includes(TEXTBAUSTEINE_VERFAHREN_DOCUMENT)
	);
	const isDirectChild = verfahrenIndex >= 0 && verfahrenIndex === nodePath.length - 2;

	// Check if it's the only sibling (no predecessor and no successor)
	const isLastSibling = !row.predecessor && !row.successor;

	return isUnderVerfahren && isDirectChild && isLastSibling;
}

function hideAction(contextItem: { annotations?: readonly { name: string; value?: string }[] }, permissions: string[]) {
	const requiredAccessRight = contextItem.annotations?.find(it => it.name === VISIBILITY_SCOPE)?.value;
	return requiredAccessRight === undefined ? false : !permissions.includes(requiredAccessRight);
}

const FvKeyMapContext = React.createContext<Record<string, string>>({});

function useFvKey(nodePath: { type: string; id: string }[]): string | undefined {
	const fvKeyMap = React.useContext(FvKeyMapContext);
	return useMemo(() => {
		const rootDocRef = nodePath.find(it => it.type === "Verfahren_D")?.id;
		return rootDocRef ? fvKeyMap[rootDocRef] : undefined;
	}, [fvKeyMap, nodePath]);
}
